// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import IsNumeric from "@insite/client-framework/Common/isNumeric";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import { ClickablePresentationProps } from "@insite/mobius/Clickable";
import Icon, { IconProps, IconWrapper } from "@insite/mobius/Icon";
import { Clickable, Typography } from "@insite/mobius/index";
import { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import { sizeVariantValues } from "@vanmeter/MobiusOverrides/FormField/formStyles";
import TextField, { TextFieldPresentationProps, TextFieldProps } from "@vanmeter/MobiusOverrides/TextField";
import { fontConfig } from "@vanmeter/XDL/constants";
import Minus from "@vanmeter/XDL/Icons/IconMinus";
import Plus from "@vanmeter/XDL/Icons/IconPlus";
import { color, mergeWithCss, xMask, xScreen } from "@vanmeter/XDL/Utilities";
import xDefaultRules, { maxQtyNumber } from "@vanmeter/XDL/Utilities/xValidate/xDefaultRules";
import React, { FC, useEffect, useMemo, useState } from "react";
import { css } from "styled-components";

interface OwnProps extends Omit<TextFieldProps, "css"> {
    onDecreaseChangeHandler?: (event: React.KeyboardEvent<HTMLInputElement>) => React.KeyboardEvent<HTMLInputElement>;
    onIncreaseChangeHandler?: (event: React.KeyboardEvent<HTMLInputElement>) => React.KeyboardEvent<HTMLInputElement>;
    showActionButton?: boolean;
    extendedStyles?: QtyFieldStyles;
    allowZero?: boolean;
    editable?: boolean;
    showLabel?: boolean;
    min?: number | string;
    max?: number | string;
    validationRules?: string;
    minimumOrderQty?: number;
    multipleSaleQty?: number;
    onChange?: any;
}

type Props = OwnProps;

export interface QtyFieldStyles {
    editableQuantityTextField?: TextFieldPresentationProps;
    container?: InjectableCss;
    wrapper?: InjectableCss;
    actionClickable?: ClickablePresentationProps;
    countIcon?: IconProps;
    qtyLabel?: TypographyProps;
    valueText?: TypographyProps;
}

export const qtyFieldStyles: QtyFieldStyles = {
    container: {
        css: css`
            display: flex;
            flex-direction: column;
        `,
    },
    wrapper: {
        css: css`
            display: flex;
            width: 100%;
            position: relative;
            border: 1px solid ${color("common.ui5")};
            height: ${sizeVariantValues.small.height}px;
            max-width: 100px;
            border-radius: 2px;
            ${({ showActionButton }: any) =>
                !showActionButton &&
                css`
                    max-width: 36px;
                `}

            &:not([class^="FormFieldLabel"]) {
                border: 1px solid ${color("common.ui5")};
            }
            &:has(input:disabled) {
                cursor: not-allowed;
                opacity: 0.33;
                & > button {
                    opacity: 1;
                }
            }
        `,
    },
    editableQuantityTextField: {
        mergeCss: true,
        backgroundColor: "transparent",
        sizeVariant: "small",
        labelProps: {
            css: css`
                display: none;
            `,
        },
        cssOverrides: {
            formField: css`
                && input:not(.react-datetime-picker__inputGroup__input):not([name="datetime"]) {
                    &,
                    &:active,
                    &:hover,
                    &:focus {
                        outline: none;
                        border: 0;
                        box-shadow: none;
                        &:not(:disabled) {
                            border: 0;
                        }
                    }
                }
            `,
            formInputWrapper: css`
                &&&& {
                    margin-top: 0;
                }
            `,
            inputSelect: css`
                font-size: ${fontConfig.smallSingleLine.size}px;
                line-height: ${fontConfig.smallSingleLine.lineHeight};
                height: calc(${sizeVariantValues.small.height}px - 2px);
                text-align: center;
                &&& {
                    &,
                    &:hover,
                    &:focus {
                        border: none;
                        padding: 0;
                        border-radius: 0;
                    }
                }

                &:disabled {
                    opacity: 1;
                }
            `,
        },
    },
    actionClickable: {
        css: css`
            background-color: ${color("common.white")};
            display: flex;
            min-width: 25px;
            align-items: center;
            height: 100%;
            justify-content: center;

            &:disabled {
                ${IconWrapper} {
                    opacity: 0.33;
                }
            }
            &:first-of-type {
                border-right: 1px solid ${color("common.ui5")};
                background: transparent;
                border-radius: 1px 0 0 1px;
            }

            &:last-of-type {
                border-left: 1px solid ${color("common.ui5")};
                background: transparent;
                border-radius: 0 1px 1px 0;
            }

            &:hover:not(:disabled) {
                background: ${color("common.ui15")};
                border-color: ${color("common.ui15")};
                ${IconWrapper}, svg {
                    color: ${color("common.white")};
                }
            }
        `,
    },
    countIcon: {
        color: "common.ui5",
        width: 11,
        height: 13,
        mergeCss: true,
    },
    valueText: {
        ...fontConfig.regularSingleLine,
        weight: fontConfig.weight.bold,
        css: css`
            display: flex;
            gap: 4px;
            align-items: center;
        `,
    },
    qtyLabel: {
        ...fontConfig.xSmallSingleLine,
        weight: fontConfig.weight.regular,
        css: css`
            display: none;
            ${xScreen("lte", "md")} {
                margin-bottom: 4px;
                display: block;
            }
        `,
    },
};

const enterKey = 13;

const QtyField: FC<Props> = ({
    extendedStyles,
    onIncreaseChangeHandler,
    onDecreaseChangeHandler,
    disabled: disabledProps = false,
    value: currentValue,
    onChange: onChangeTextField,
    onBlur: onBlurTextField,
    onClick: onClickTextField,
    onKeyDown: onKeyDownTextField,
    label: labelOverride,
    showActionButton = true,
    allowZero = false,
    editable = true,
    showLabel = true,
    min = 0,
    max = maxQtyNumber,
    validationRules,
    minimumOrderQty,
    multipleSaleQty,
    ...qtyFieldsProps
}) => {
    const styles = useMemo(() => mergeWithCss(qtyFieldStyles, extendedStyles), []);
    const [callOnSubmitSafety, setCallOnSubmitSafety] = useState(false);

    let minQty = 0;
    let maxQty = maxQtyNumber;
    (validationRules || xDefaultRules["qty"]).split("|").forEach(item => {
        const [attr, val] = item.split(":");
        if (attr === "min") {
            minQty = Math.max(Number(min), +val);
        }
        if (attr === "max") {
            maxQty = Math.min(Number(max), +val);
        }
    }, {});

    const disabled = !callOnSubmitSafety && disabledProps;

    /**
     * UseEffect to wait until data change in cartQuantity state
     * then we do submit
     * src/FrontEnd/modules/client-framework/src/Common/Hooks/useAccessibleSubmit.ts
     */
    useEffect(() => {
        if (callOnSubmitSafety) {
            onBlurHandler();
            setCallOnSubmitSafety(false);
        }
    }, [currentValue]);

    const onClickHandler = (event: React.MouseEvent<HTMLInputElement, MouseEvent>) => {
        event.currentTarget.focus();
        onClickTextField?.(event);
    };

    const onChangeHandler = (event: React.ChangeEvent<HTMLInputElement>, force = false) => {
        event.target.value = xMask.qty(event.target.value);
        onChangeTextField?.(event, { force });
    };

    useEffect(() => {
        if (minQty > (currentValue as number) && !allowZero) {
            onChangeHandler?.(
                {
                    target: { value: minQty.toString() },
                    currentTarget: { value: minQty.toString() },
                } as any,
                true,
            );
        }
    }, [minQty, maxQty]);

    const onKeyDownHandler = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.keyCode === enterKey) {
            event.stopPropagation();
            onBlurTextField?.(event as any);
        }
        onKeyDownTextField?.(event);
    };

    const onBlurHandler = (event?: React.ChangeEvent<HTMLInputElement>) => {
        if (event?.currentTarget) {
            event.currentTarget.value = xMask.qty(event.currentTarget.value);
            onChangeHandler(event);
        }
        onBlurTextField?.(event as any);
    };

    const onDecreaseClickHandler = (event: any) => {
        if (disabled) {
            return;
        }

        let nextValue;
        // do rounding in case we have minimumOrderedQuantity and MultipleSaleQty
        if (multipleSaleQty && multipleSaleQty > 1) {
            nextValue =
                currentValue && IsNumeric(`${currentValue}`) && Number(currentValue) - multipleSaleQty >= minQty
                    ? Number(currentValue) - multipleSaleQty
                    : multipleSaleQty;
        } else {
            nextValue =
                currentValue && IsNumeric(`${currentValue}`) && Number(currentValue) - 1 >= minQty
                    ? Number(currentValue) - 1
                    : undefined;
        }

        if (allowZero && nextValue === undefined) {
            return;
            // eslint-disable-next-line no-else-return
        } else if (!allowZero && !nextValue) {
            return;
        }

        event.currentTarget.value = xMask.qty(`${nextValue}`);
        event.target.value = xMask.qty(`${nextValue}`);

        if (onDecreaseChangeHandler) {
            event = onDecreaseChangeHandler(event);
        }

        setCallOnSubmitSafety(true);
        onChangeHandler(event, true);
    };

    const onIncreaseClickHandler = (event: any) => {
        if (disabled) {
            return;
        }

        let nextValue;
        // do rounding in case we have minimumOrderedQuantity and MultipleSaleQty
        if (multipleSaleQty && multipleSaleQty > 1) {
            nextValue =
                currentValue && IsNumeric(`${currentValue}`) && Number(currentValue) + multipleSaleQty <= maxQty
                    ? Number(currentValue) + multipleSaleQty
                    : undefined;
        } else {
            nextValue =
                currentValue && IsNumeric(`${currentValue}`) && Number(currentValue) + 1 <= maxQty
                    ? Number(currentValue) + 1
                    : undefined;
        }

        if (!nextValue) {
            return;
        }

        event.currentTarget.value = xMask.qty(`${nextValue}`);
        event.target.value = xMask.qty(`${nextValue}`);

        if (onIncreaseChangeHandler) {
            event = onIncreaseChangeHandler(event);
        }
        setCallOnSubmitSafety(true);
        onChangeHandler(event, true);
    };

    return (
        <StyledWrapper {...styles.container}>
            {editable ? (
                <>
                    {showLabel && (
                        <Typography {...styles.qtyLabel} {...{ showLabel }}>
                            {labelOverride || translate("QTY")}
                        </Typography>
                    )}
                    <StyledWrapper {...styles.wrapper} data-test-selector="quantity-field" {...{ showActionButton }}>
                        {showActionButton && (
                            <Clickable
                                {...styles.actionClickable}
                                disabled={disabled || Number(currentValue) === minQty}
                                onClick={onDecreaseClickHandler}
                                aria-label="qty-decrement"
                            >
                                <Icon {...styles.countIcon} src={Minus} />
                            </Clickable>
                        )}
                        <TextField
                            type="qty"
                            disabled={disabled}
                            value={currentValue}
                            onChange={(e: any) => onChangeHandler(e)}
                            onBlur={onBlurHandler}
                            onClick={onClickHandler}
                            onKeyDown={onKeyDownHandler}
                            label={labelOverride || translate("QTY")}
                            {...styles.editableQuantityTextField}
                            {...qtyFieldsProps}
                            validationRules={validationRules}
                            showError={false}
                        />
                        {showActionButton && (
                            <Clickable
                                {...styles.actionClickable}
                                disabled={disabled || Number(currentValue) >= maxQty}
                                onClick={onIncreaseClickHandler}
                                aria-label="qty-increment"
                            >
                                <Icon {...styles.countIcon} src={Plus} />
                            </Clickable>
                        )}
                    </StyledWrapper>
                </>
            ) : (
                <>
                    <VisuallyHidden as="label">{labelOverride || translate("QTY")}</VisuallyHidden>
                    {showLabel && <Typography {...styles.qtyLabel}>{labelOverride || translate("QTY")}</Typography>}
                    <Typography {...styles.valueText}>{currentValue}</Typography>
                </>
            )}
        </StyledWrapper>
    );
};

export default QtyField;
